import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Users, Award, FileText, TrendingUp, BookOpen } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';

const GovernanceQuality = () => {
  const clinicalGovernance = [
    {
      icon: Shield,
      title: 'RMN-Led Clinical Oversight',
      description: 'Registered Mental Health Nurse leadership providing expert clinical guidance'
    },
    {
      icon: Users,
      title: 'Supervision & Reflective Practice',
      description: 'Regular supervision and reflective practice sessions for all staff'
    },
    {
      icon: FileText,
      title: 'Incident Reporting',
      description: 'Systematic incident reporting and learning culture'
    },
    {
      icon: TrendingUp,
      title: 'Risk Management',
      description: 'Comprehensive risk assessment and management processes'
    }
  ];

  const workforceGovernance = [
    'Safer recruitment processes',
    'Enhanced DBS checks',
    'Mandatory and role-specific training',
    'Ongoing professional development',
    'Clinical supervision',
    'Performance management'
  ];

  const qualityAssurance = [
    'Feedback from service users and stakeholders',
    'Regular audits and service reviews',
    'Continuous improvement planning',
    'Outcome monitoring',
    'Complaints and compliments analysis',
    'External quality assessments'
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#E8F4FC] to-[#F0EDF7] py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <Award className="w-16 h-16 text-[#4A90E2] mx-auto" />
            <h1 className="heading-hero">Governance & Quality</h1>
            <p className="body-large">
              A robust governance framework ensuring services are safe, effective, and continuously improving.
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="body-large mb-4">
              Three Spheres Healthcare operates a robust governance framework to ensure services are safe, effective, and continuously improving.
            </p>
          </div>
        </div>
      </section>

      {/* Clinical Governance */}
      <section className="section bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="heading-2 mb-12 text-center">Clinical Governance</h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {clinicalGovernance.map((item, index) => {
              const Icon = item.icon;
              return (
                <Card key={index} className="p-8 border-0 hover:shadow-lg transition-all">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#4A90E2] to-[#7B68BE] rounded-lg flex items-center justify-center mb-4">
                    <Icon className="text-white" size={24} />
                  </div>
                  <h3 className="heading-3 mb-3">{item.title}</h3>
                  <p className="text-sm text-gray-600">{item.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Workforce Governance */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="heading-2 mb-12 text-center">Workforce Governance</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {workforceGovernance.map((item, index) => (
                <Card key={index} className="p-6 border-0 bg-[#E8F4FC]">
                  <div className="flex items-center gap-3">
                    <Users className="text-[#4A90E2] flex-shrink-0" size={20} />
                    <span className="text-gray-800">{item}</span>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quality Assurance */}
      <section className="section bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="heading-2 mb-12 text-center">Quality Assurance</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {qualityAssurance.map((item, index) => (
                <Card key={index} className="p-6 border-0 bg-[#F0EDF7]">
                  <div className="flex items-center gap-3">
                    <BookOpen className="text-[#7B68BE] flex-shrink-0" size={20} />
                    <span className="text-gray-800">{item}</span>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Closing Statement */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="p-8 md:p-12 border-0 bg-gradient-to-br from-[#E8F4FC] to-[#F0EDF7] text-center mb-8">
              <Shield className="w-12 h-12 text-[#4A90E2] mx-auto mb-6" />
              <p className="body-large text-gray-800">
                Governance arrangements are aligned with Care Quality Commission (CQC) expectations and best practice guidance.
              </p>
            </Card>

            {/* Registered Manager */}
            <Card className="p-8 border-0 bg-[#E8F4FC]">
              <div className="flex items-start gap-4">
                <Users className="text-[#4A90E2] flex-shrink-0 mt-1" size={32} />
                <div>
                  <h3 className="font-semibold text-lg mb-3 text-gray-900">Registered Manager</h3>
                  <p className="text-gray-700">
                    Registered Manager details will be published in line with CQC registration requirements.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-gradient-to-r from-[#4A90E2] to-[#7B68BE] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="heading-2 text-white">Learn More About Our Standards</h2>
            <p className="body-large text-white/90">
              Contact us for more information about our governance and quality assurance processes.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-[#4A90E2] hover:bg-gray-100">
                Get in Touch
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GovernanceQuality;
